import { Forgot, Reset } from './pages/Recovery.tsx';
import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth.tsx';
import { NavRoot } from './lib/nav/react.tsx';
import { AudioRoot } from './lib/audio/react.tsx';
import { Page, Shell } from './components/Shell.tsx';
import { ConfirmProvider, Loading, ToastProvider } from './components/ui.tsx';
import { Login } from './pages/Login.tsx';
import { Invite } from './pages/Invite.tsx';
import { Home } from './pages/Home.tsx';
import { Library } from './pages/Library.tsx';
import { GamePage } from './pages/Game.tsx';

const Play = lazy(() => import('./player/PlayPage.tsx'));
const Social = lazy(() => import('./pages/Social.tsx'));
const SettingsPage = lazy(() => import('./pages/Settings.tsx'));
const Misc = lazy(() => import('./pages/Misc.tsx'));
const Admin = lazy(() => import('./pages/admin/AdminRoutes.tsx'));
const Room = lazy(() => import('./pages/Room.tsx').then((m) => ({ default: m.RoomPage })));

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>
            <NavRoot>
              <AudioRoot />
              <Suspense fallback={<Loading />}>
                <Routes>
                  <Route path="/forgot" element={<Forgot />} />
                  <Route path="/reset/:token" element={<Reset />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/invite/:token" element={<Invite />} />
                  <Route
                    path="/play/:id"
                    element={
                      <RequireAuth>
                        <Play />
                      </RequireAuth>
                    }
                  />
                  <Route
                    element={
                      <RequireAuth>
                        <Shell />
                      </RequireAuth>
                    }
                  >
                    {/* Console screens: full-bleed, stage-driven. */}
                    <Route index element={<Home />} />
                    <Route path="library" element={<Library />} />
                    <Route path="collections/:slug" element={<Library />} />
                    <Route path="game/:id" element={<GamePage />} />
                    {/* Conventional screens. */}
                    <Route element={<Page />}>
                      <Route path="friends" element={<Social view="friends" />} />
                      <Route path="messages" element={<Social view="messages" />} />
                      <Route path="messages/:userId" element={<Social view="thread" />} />
                      <Route path="notifications" element={<Social view="notifications" />} />
                      <Route path="u/:id" element={<Social view="profile" />} />
                      <Route path="settings/*" element={<SettingsPage />} />
                      <Route path="room/:id" element={<Room />} />
                      <Route path="requests" element={<Misc view="requests" />} />
                      <Route path="invites" element={<Misc view="invites" />} />
                      <Route path="admin/*" element={<Admin />} />
                      <Route path="*" element={<Misc view="notfound" />} />
                    </Route>
                  </Route>
                </Routes>
              </Suspense>
            </NavRoot>
          </AuthProvider>
        </ConfirmProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
